/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';

import { StorageDirectUrl, StorageFile } from '../../../types';
import { RestService } from './rest.service';
import { Observable, of } from 'rxjs';
import { LoggerService } from './logger.service';
import { map, tap } from 'rxjs/operators';
import { DownloadService } from './download.service';

@Injectable()
export class StorageService {

    private iconMap = new Map<string, string>();

    public constructor(
        private logger: LoggerService,
        private rest: RestService,
        private downloadService: DownloadService
    ) {}

    /**
     * @param {File | Blob} file
     * @param {string} filename
     * @return {Observable<any>}
     */
    public storeFile(file: File | Blob, filename?: string): Observable<any> {
        const fd = new FormData();
        fd.append('file', file, filename);
        return this.rest.apiPost('storage/store', fd);
    }

    /**
     * Returns a file from backend storage (as a Base64encoded string)
     * @param {string} hash
     * @return {Observable<StorageFile|StorageDirectUrl>}
     */
    public retrieveFile(hash: string): Observable<StorageFile | StorageDirectUrl> {
        return this.rest.apiGet(`storage/retrieve/${hash}`, {});
    }

    public handleBlob(fileObject: StorageFile): Observable<any> {
        return of(fileObject).pipe(
            map((obj) => ({
                blob: this.downloadService.createBlobFromDataURL(obj.content),
                filename: fileObject.metadata.originalName,
            })),
            tap((downloadObject) => {
                this.downloadService.downloadBlob(downloadObject.blob, downloadObject.filename);
            })
        );
    }

    public handleUrl(fileObject: StorageDirectUrl): Observable<any> {
        return of(fileObject).pipe(
            tap((urlObject: StorageDirectUrl) => {
                this.downloadService.downloadFromUrl(urlObject.url.url, urlObject.metadata.originalName);
            })
        );
    }

    /**
     * Removes a file identified by @ref hash from the backend store.
     * @param {string} hash
     * @return {Observable<any>}
     */
    public removeFile(hash: string): Observable<any> {
        return this.rest.apiPost(`storage/remove/${hash}`, {});
    }

    public getImageThumbnailUrl(hash: string, width?: number): Observable<string> {
        const widthParam = width ? `?width=${width}` : '';
        return this.rest.apiGet(`storage/thumbnail/${hash}${widthParam}`, null, { responseType: 'text' });
    }

    public getFileMimeType(hash): Observable<string> {
        return this.rest.apiGet(`storage/type/${hash}`, null, { responseType: 'text' });
    }

    public mimeTypeToIcon(mimeType: string): string {
        switch (mimeType) {
            case 'image/png':
            case 'image/jpeg':
            case 'image/gif':
            case 'image/tiff':
                return 'image';
            case 'application/pdf':
                return 'pdf';
            default:
                return 'file';
        }
    }
}
